import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, DatePicker, LoadingSpinner, MultiSelect } from 'yankees-react-utils';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Stats, useOktaQuery } from '../../../../data';

const StyledHeader = styled.div`
    position: relative;
    z-index: 2;
    .dropdown-toggle.form-control {
        max-width: 100px;
    }

    .form-control {
        max-width: 130px;
    }
`;

const Header = ({ playerId, defaultLevelId, filter, setFilter, levels, setLevels }) => {
    const { t: translate } = useTranslation();

    const [innerLevelIds, setInnerLevelIds] = useState(filter.levelIds ?? []);
    const [innerStartDate, setInnerStartDate] = useState(filter.startDate);
    const [innerEndDate, setInnerEndDate] = useState(filter.endDate);

    const { data: battingSeasons, isLoading } = useOktaQuery(
        ['battingSeasons', playerId],
        (t) => Stats.seasonBatting(t, { playerid: playerId }),
        { enabled: !!playerId, staleTime: 0, cacheTime: 0 },
    );

    useEffect(() => {
        if (battingSeasons && battingSeasons.length) {
            const localLevels = battingSeasons.map((bs) => bs.Level);
            const newLevels = [...new Set(localLevels.map((lv) => lv.LevelID))]
                .map((levelId) => localLevels.find((lv) => lv.LevelID === levelId))
                .sort((a, b) => a.SortOrder - b.SortOrder)
                .map((lv) => ({
                    name: lv.Abbr,
                    value: lv.LevelID,
                }));
            const defaultLevel = newLevels.find((lv) => lv.value === defaultLevelId) ?? newLevels[0];
            const nextLevelIds = defaultLevel?.value ? [defaultLevel.value] : [];
            setLevels(newLevels);
            setInnerLevelIds(nextLevelIds);
            setFilter((prev) => ({ ...prev, levelIds: nextLevelIds }));
        }
    }, [battingSeasons, defaultLevelId, setFilter, setLevels]);

    useEffect(() => {
        setInnerLevelIds(filter.levelIds ?? []);
    }, [filter.levelIds]);

    useEffect(() => {
        setInnerStartDate(filter.startDate);
    }, [filter.startDate]);

    useEffect(() => {
        setInnerEndDate(filter.endDate);
    }, [filter.endDate]);

    const onApply = () => {
        setFilter({
            ...filter,
            levelIds: innerLevelIds,
            startDate: innerStartDate,
            endDate: innerEndDate,
        });
    };

    return (
        <StyledHeader className="d-flex justify-content-between align-items-center mb-1">
            <h3>{translate('sections.reports.pitcher.bat-tracking-title')}</h3>
            <div className="rounded py-2 px-3 d-flex align-items-center bg-white" style={{ border: '1px solid rgb(188,200,209)' }}>
                <div className="d-flex align-items-center me-4">
                    <span className="me-1">{translate('baseball.level_one')}:</span>
                    {isLoading ? <LoadingSpinner /> : <MultiSelect value={innerLevelIds} setValue={setInnerLevelIds} options={levels} disabled={!playerId} />}
                </div>
                <div className="d-flex align-items-center">
                    <DatePicker
                        value={innerStartDate}
                        onSelect={setInnerStartDate}
                        size="sm"
                        maxDate={innerEndDate}
                    />
                    <div className="mx-2">{translate('common.to')}</div>
                    <DatePicker
                        value={innerEndDate}
                        onSelect={setInnerEndDate}
                        size="sm"
                        minDate={innerStartDate}
                    />
                </div>
                <Button className="ms-5" variant="primary" onClick={onApply}>{translate('common.apply')}</Button>
            </div>
        </StyledHeader>
    );
};

Header.propTypes = {
    playerId: PropTypes.number.isRequired,
    defaultLevelId: PropTypes.number,
    filter: PropTypes.shape({
        levelIds: PropTypes.arrayOf(PropTypes.number),
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }).isRequired,
    setFilter: PropTypes.func.isRequired,
    levels: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
    })).isRequired,
    setLevels: PropTypes.func.isRequired,
};

Header.defaultProps = {
    defaultLevelId: null,
};

export default Header;
